"use client"

import * as React from "react"
import { Tooltip as TooltipPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"

/**
 * Story 24.2 — the shared Tooltip (DESIGN.md › Components › Tooltip).
 *
 * - Opens after 500ms of hover, at once on keyboard focus; Esc closes it and
 *   leaves focus where it was; the content is hoverable (pointer can move
 *   onto it) and never times out.
 * - Self-providing: a `Tooltip` with none of our providers above it wraps
 *   itself in one, so callers never hand-mount `TooltipProvider` (an explicit
 *   provider still works, e.g. to share the skip-delay across a rail).
 * - Radius: DESIGN `md` (6px) is `rounded-lg` here (`rounded-md` is 4px).
 * - Touch: Radix ignores touch hover, so touch gets `InfoTooltip`
 *   (`./info-tooltip.tsx`, tap toggles) or, on a disabled-with-reason Button,
 *   a long-press.
 */

const TOOLTIP_DELAY_MS = 500

const HasTooltipProvider = React.createContext(false)

function TooltipProvider({
  delayDuration = TOOLTIP_DELAY_MS,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <HasTooltipProvider.Provider value={true}>
      <TooltipPrimitive.Provider
        data-slot="tooltip-provider"
        delayDuration={delayDuration}
        {...props}
      />
    </HasTooltipProvider.Provider>
  )
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  const provided = React.useContext(HasTooltipProvider)
  const root = <TooltipPrimitive.Root data-slot="tooltip" {...props} />
  return provided ? root : <TooltipProvider>{root}</TooltipProvider>
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

function TooltipContent({
  className,
  sideOffset = 4,
  collisionPadding = 8,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(
          "z-50 w-fit max-w-[280px] origin-(--radix-tooltip-content-transform-origin) rounded-lg border border-line-strong bg-surface-raised px-3 py-2 type-body-sm break-words text-ink shadow-float",
          "animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          "motion-reduce:animate-none motion-reduce:data-[state=closed]:animate-none",
          className
        )}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

type Side = React.ComponentProps<typeof TooltipPrimitive.Content>["side"]

type WithTooltipProps =
  | {
      /**
       * The tooltip IS the trigger's label (an icon-only button): the child
       * gets `aria-label` = content and no duplicate `aria-describedby`. The
       * content must be a string, so the trigger always has a name.
       */
      asLabel: true
      content: string
      side?: Side
      sideOffset?: number
      children: React.ReactElement
    }
  | {
      asLabel?: false
      content: React.ReactNode
      side?: Side
      sideOffset?: number
      children: React.ReactElement
    }

/**
 * Wrap one trigger element in the shared Tooltip. `sideOffset` (default 4)
 * moves it further off the trigger — the icon rail uses it so the tooltip
 * clears the rail's own edge, not just the icon's (Story 24.6).
 */
function WithTooltip({ content, asLabel = false, side, sideOffset, children }: WithTooltipProps) {
  const labelProps =
    asLabel && typeof content === "string"
      ? { "aria-label": content, "aria-describedby": undefined }
      : {}
  return (
    <Tooltip>
      <TooltipTrigger asChild {...labelProps}>
        {children}
      </TooltipTrigger>
      <TooltipContent side={side} sideOffset={sideOffset}>{content}</TooltipContent>
    </Tooltip>
  )
}

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  WithTooltip,
}
